import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Coins, Bitcoin, Users, Gift, Copy, Loader2, TrendingUp, Wallet, Database, Send, Banknote, DollarSign, Sparkles, ShieldCheck, Upload, GraduationCap, BadgeCheck, Zap } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError, rememberRedirect } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Price } from "@/components/Price";
import { FairPriceBadge } from "@/components/FairPriceBadge";
import { BTC_ENABLED } from "@/lib/flags";

const ACCOUNT_LABELS = { personal: "Personal", small: "Small Org", mid: "Mid-size Org", enterprise: "Enterprise" };

const DISCOUNT_CATS = [
  { key: "student", label: "Student", pct: 25 },
  { key: "teacher", label: "Teacher / Educator", pct: 12 },
  { key: "yoga", label: "Yoga Teacher", pct: 12 },
  { key: "doctor", label: "Doctor / Medical", pct: 15 },
  { key: "veteran", label: "Retired Armed Forces", pct: 15 },
  { key: "police", label: "Police Personnel", pct: 15 },
];

function DiscountPanel({ onApproved }) {
  const { user, refreshUser } = useApp();
  const [me, setMe] = useState(null);
  const [cat, setCat] = useState("student");
  const [idUrl, setIdUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) api.get("/discounts/me").then((r) => setMe(r.data)).catch(() => {}); }, [user]);
  if (!user) return null;
  const active = me?.active;

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("category", "discount_id");
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setIdUrl(data.url); setFileName(file.name);
      toast.success("ID uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!idUrl) { toast.error("Upload your ID proof first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/discounts/apply", { category: cat, id_url: idUrl });
      const r = await api.get("/discounts/me"); setMe(r.data);
      if (data.approved) { toast.success(`Verified! ${data.pct}% ${DISCOUNT_CATS.find((c) => c.key === cat)?.label} discount unlocked.`); refreshUser?.(); onApproved?.(); }
      else if (data.status === "rejected") toast.error(`My Kind couldn't verify this: ${data.verdict?.reason || "please upload a clearer ID."}`);
      else toast("Submitted for review.");
      setIdUrl(""); setFileName("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (active) {
    return (
      <div className="rud-card rounded-2xl p-5 flex items-center gap-3" data-testid="discount-active">
        <div className="w-10 h-10 rounded-xl bg-brand/15 grid place-items-center text-brand shrink-0"><BadgeCheck size={20} /></div>
        <div><div className="font-medium text-sm">{active.pct}% {active.label} discount active</div>
          <div className="text-xs text-sec">My Kind verified your ID — the discount is applied to every credit pack above.</div></div>
      </div>
    );
  }

  return (
    <div className="rud-card rounded-2xl p-5" data-testid="discount-panel">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-1"><GraduationCap size={14} /> ID discount — verified instantly by My Kind</div>
      <p className="text-sm text-sec">Students 25% · Teachers 12% · Doctors, Retired Armed Forces & Police 15%. Upload your ID and My Kind's AI validates & approves it on the spot.</p>
      <div className="grid sm:grid-cols-3 gap-3 mt-3">
        <select data-testid="discount-category" value={cat} onChange={(e) => setCat(e.target.value)} className="rud-input rounded-lg px-3 py-2.5 text-sm">
          {DISCOUNT_CATS.map((c) => <option key={c.key} value={c.key}>{c.label} — {c.pct}%</option>)}
        </select>
        <label className="btn-outline rounded-lg h-11 px-3 text-sm flex items-center justify-center gap-1.5 cursor-pointer" data-testid="discount-upload">
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {fileName ? "Change ID" : "Upload ID proof"}
          <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        <button data-testid="discount-submit" onClick={submit} disabled={busy || !idUrl} className="btn-primary rounded-lg h-11 px-4 text-sm flex items-center justify-center gap-1.5 disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Verify & apply
        </button>
      </div>
      {fileName && <div className="text-[11px] text-sec mt-2 flex items-center gap-1.5"><BadgeCheck size={12} className="text-brand" /> {fileName} ready</div>}
      {me?.application?.status === "rejected" && <div className="text-[11px] text-red-400 mt-2">Last attempt rejected: {me.application.ai_verdict?.reason}. Try a clearer photo of your ID.</div>}
      {me?.application?.status === "pending" && <div className="text-[11px] text-sec mt-2">Your application is under review.</div>}
    </div>
  );
}

function AutoTopupPanel() {
  const { user, refreshUser } = useApp();
  const [on, setOn] = useState(!!user?.auto_topup_enabled);
  const [amount, setAmount] = useState(user?.auto_topup_amount || 5);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setOn(!!user?.auto_topup_enabled); setAmount(user?.auto_topup_amount || 5); }, [user?.auto_topup_enabled, user?.auto_topup_amount]);
  if (!user) return null;

  const save = async (nextOn, nextAmt) => {
    const amt = Math.max(5, Math.floor(nextAmt || 5));
    setBusy(true);
    try {
      await api.post("/me/auto-topup", { enabled: nextOn, amount: amt });
      await refreshUser?.();
      toast.success(nextOn ? `Auto-recharge on — ${amt} credits added automatically when you run low.` : "Auto-recharge turned off.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rud-card rounded-2xl p-6 mt-4" data-testid="auto-topup-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="max-w-lg">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-1"><Zap size={14} /> Auto-recharge</div>
          <p className="text-sec text-sm">Never get interrupted mid-search. When your balance runs low, we'll automatically add credits so your searches, saves &amp; contributions just keep flowing.</p>
        </div>
        <button
          data-testid="auto-topup-toggle"
          onClick={() => { const n = !on; setOn(n); save(n, amount); }}
          disabled={busy}
          className={`w-12 h-7 rounded-full shrink-0 relative transition-colors ${on ? "bg-brand" : "bg-white/15"}`}
        >
          <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${on ? "left-6" : "left-1"}`} />
        </button>
      </div>
      <div className={`flex items-center gap-3 mt-4 transition-opacity ${on ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
        <label className="text-[11px] uppercase tracking-widest text-sec">Recharge amount</label>
        <div className="flex items-center gap-2 rud-input rounded-lg px-3 py-2">
          <Coins size={14} className="text-brand" />
          <input data-testid="auto-topup-amount" type="number" min={5} value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            onBlur={() => on && save(true, amount)}
            className="bg-transparent border-none outline-none w-20 text-sm" />
          <span className="text-xs text-sec">credits ($1 each)</span>
        </div>
        <span className="text-[11px] text-sec">Minimum 5 · billed to your saved method (demo).</span>
      </div>
    </div>
  );
}


export default function Economy() {
  const { t, user, setUser, currency, setCurrency, meta, countryObj, cryptos } = useApp();
  const curSym = (() => { try { return (0).toLocaleString(undefined, { style: "currency", currency: countryObj?.currency || currency || "USD" }).replace(/[0-9.,\s]/g, "") || "$"; } catch { return "$"; } })();
  const nav = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [earn, setEarn] = useState(null);
  const [btc, setBtc] = useState(null);
  const [amount, setAmount] = useState(100);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState("money");
  const [wallet, setWallet] = useState("");
  const [xfer, setXfer] = useState({ to: "", amount: 50 });
  const [bonusCredits, setBonusCredits] = useState(50);

  const currencies = meta?.currencies || {};
  useEffect(() => { api.get(`/pricing?currency=${currency}`).then((r) => setPricing(r.data)); }, [currency]);
  const loadEarn = () => { if (user) api.get("/earn/summary").then((r) => { setEarn(r.data); setWallet(r.data.payout_wallet || ""); }).catch(() => {}); };
  useEffect(loadEarn, [user]);

  const copy = async (text, label) => { try { await navigator.clipboard.writeText(text); toast(label); } catch { toast.error("Copy failed"); } };

  const buy = async (usd) => {
    if (!user) { rememberRedirect(); nav("/auth"); return; }
    setBusy(true);
    try { const { data } = await api.post("/credits/purchase", { amount_usd: usd }); setUser({ ...user, credits: data.credits }); toast.success(`+${data.purchased} credits (mock)`); loadEarn(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const genBtc = async () => {
    if (!user) { nav("/auth"); return; }
    try { const { data } = await api.get(`/payments/bitcoin?amount_usd=${amount}`); setBtc(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setAccount = async (type) => {
    try { await api.post("/account/type", { account_type: type }); setUser({ ...user, account_type: type }); loadEarn(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const cashout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/earn/cashout", { method });
      setUser({ ...user, credits: data.credits });
      if (method === "credits") {
        toast.success(`+${data.credits_added} credits (incl. ${data.bonus_pct}% bonus)`);
      } else {
        const kept = data.credits_kept ? ` · ${data.credits_kept} credits kept in-app` : "";
        if (method === "btc") toast.success(`Cashed out ${data.btc_amount} BTC (${data.payout_pct}%)${kept} — mock`);
        else toast.success(`Cashed out $${data.amount_usd} (${data.payout_pct}%)${kept} — mock`);
      }
      loadEarn();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const transfer = async () => {
    if (!xfer.to.trim()) { toast.error("Recipient email or referral code."); return; }
    try { const { data } = await api.post("/credits/transfer", { to: xfer.to, amount: Number(xfer.amount) }); setUser({ ...user, credits: data.credits }); toast.success(`Sent ${data.transferred} credits to ${data.to}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveWallet = async () => { try { await api.put("/earn/wallet", { address: wallet }); toast.success("Payout wallet saved (private)"); loadEarn(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const bonusPay = (Math.max(0, Number(bonusCredits) || 0) * 0.85).toFixed(2);
  const bonusSave = (Math.max(0, Number(bonusCredits) || 0) * 0.15).toFixed(2);
  const buyWithBonus = async () => {
    if (!user) { nav("/auth"); return; }
    const n = Math.floor(Number(bonusCredits) || 0);
    if (n <= 0) { toast.error("Enter how many credits to buy."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/credits/buy-with-bonus", { credits: n });
      setUser({ ...user, credits: data.credits });
      toast.success(`+${data.purchased} credits · paid $${data.usd_paid} from bonuses · saved $${data.usd_saved}`);
      loadEarn();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const referralLink = earn ? `${window.location.origin}/auth?ref=${earn.referral_code}` : "";
  const inviteMsg = [
    "🌍 Join me on My Kind — the App of Everything: AI search, health, learning, community travel & banking, all in one — ad-free, clutter-free & data-sovereign.",
    "",
    "✨ FREE to start (no card needed). Every new member gets:",
    "• 1 free AI search — ask anything, clutter- & ad-free",
    "• 1 free Save & Earn — save your answer for the community and earn 15% every time someone opens it",
    "• 1 free Galactopedia contribution — add knowledge to the shared library and earn credits",
    "• 1 free Product Safety scan — snap any food/product label to reveal harmful ingredients, calories & cleaner swaps",
    "• Free Community Travel posting — offer or find rides in your community",
    "",
    "The best part: you OWN your data and EARN from it. Save useful searches, knowledge & scans for the community and keep earning credits as more people use them — real income while you help others.",
    "",
    "Join with my link and we both get bonus credits 👇",
  ].join("\n");
  const inviteShort = "Join me on My Kind — the App of Everything. Free AI search, health scans, learning & community earning — you own your data & earn from it. Bonus credits inside 👇";
  const pct = earn ? Math.min(100, (earn.total_earnings / earn.threshold) * 100) : 0;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter">My Earnings</h1>
          <p className="text-sec mt-3 max-w-xl">Earn from referrals and your anonymized data. Cash out <b className="text-brand">75%</b> as money{BTC_ENABLED ? " or Bitcoin" : ""} (the other 25% stays as in-app credits), or take <b className="text-brand">100% as credits</b> (+15% bonus). Share credits with anyone.</p>
        </div>
        <Select value={currency} onValueChange={setCurrency}>
          <SelectTrigger data-testid="currency-select" className="rud-input rounded-lg w-40"><SelectValue /></SelectTrigger>
          <SelectContent className="glass border-rud text-[var(--rud-text)] max-h-72">
            {Object.keys(cryptos || {}).map((tk) => <SelectItem key={tk} value={tk} className="focus:bg-white/10">{cryptos[tk].symbol ? `${cryptos[tk].symbol} ` : ""}{tk}</SelectItem>)}
            {Object.entries(currencies).map(([code, c]) => <SelectItem key={code} value={code} className="focus:bg-white/10">{code} · {c.symbol}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Wallet + earnings */}
      <div className="grid sm:grid-cols-4 gap-4 mt-8">
        <div className="rud-card rounded-2xl p-6" data-testid="wallet-credits"><Coins className="text-brand mb-3" /><div className="text-sec text-xs uppercase tracking-widest">Credits</div><div className="font-serif text-4xl mt-1">{user?.credits != null ? Math.round(user.credits).toLocaleString() : "—"}</div></div>
        <div className="rud-card rounded-2xl p-6"><Gift className="text-brand mb-3" /><div className="text-sec text-xs uppercase tracking-widest">Referral earnings</div><div className="font-serif text-4xl mt-1"><Price usd={earn?.referral_earnings ?? 0} inline primaryClass="font-serif text-4xl" subClass="text-xs text-sec" /></div></div>
        <div className="rud-card rounded-2xl p-6"><Database className="text-brand mb-3" /><div className="text-sec text-xs uppercase tracking-widest">Data earnings</div><div className="font-serif text-4xl mt-1"><Price usd={earn?.data_earnings ?? 0} inline primaryClass="font-serif text-4xl" subClass="text-xs text-sec" /></div></div>
        <div className="rud-card rounded-2xl p-6"><TrendingUp className="text-brand mb-3" /><div className="text-sec text-xs uppercase tracking-widest">Total earnings</div><div className="font-serif text-4xl mt-1"><Price usd={earn?.total_earnings ?? 0} inline primaryClass="font-serif text-4xl" subClass="text-xs text-sec" /></div></div>
      </div>

      {/* Earn & cashout */}
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <div className="rud-card rounded-2xl p-6" data-testid="cashout-panel">
          <h3 className="font-serif text-2xl mb-3 flex items-center justify-between gap-2">Cash out <FairPriceBadge compact /></h3>
          <label className="text-xs uppercase tracking-widest text-sec">Account type (sets threshold)</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            {Object.keys(ACCOUNT_LABELS).map((k) => (
              <button key={k} data-testid={`acct-${k}`} onClick={() => setAccount(k)} disabled={!user}
                className={`rounded-lg p-2 text-xs border ${earn?.account_type === k ? "border-brand bg-white/5 text-brand" : "border-rud text-sec"}`}>
                {ACCOUNT_LABELS[k]}<div className="text-[10px]"><Price usd={earn?.account_types?.[k] ?? 0} inline primaryClass="text-[10px]" showSub={false} /></div>
              </button>
            ))}
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1"><span className="text-sec">Progress to threshold</span><span className="inline-flex items-center gap-1"><Price usd={earn?.total_earnings ?? 0} inline primaryClass="text-sm" showSub={false} /> / <Price usd={earn?.threshold ?? 0} inline primaryClass="text-sm" showSub={false} /></span></div>
            <div className="h-2 rounded-full bg-elevated overflow-hidden"><div className="h-full bg-brand" style={{ width: `${pct}%` }} /></div>
          </div>
          <label className="text-xs uppercase tracking-widest text-sec mt-4 block">Payout wallet / account <span className="text-brand">· private</span></label>
          <div className="flex gap-2 mt-2">
            <input data-testid="payout-wallet" value={wallet} onChange={(e) => setWallet(e.target.value)} placeholder={BTC_ENABLED ? "BTC address or bank/UPI id" : "Bank account / UPI id"} className="rud-input flex-1 rounded-lg px-3 py-2 text-sm" />
            <button data-testid="save-wallet" onClick={saveWallet} disabled={!user} className="btn-ghost rounded-lg px-4 text-sm">Save</button>
          </div>
          <p className="text-[11px] text-sec mt-1">Only you can see this. It's never shown to other users.</p>

          <label className="text-xs uppercase tracking-widest text-sec mt-4 block">Payout method</label>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {[["money", `Cash 75% (${curSym})`, Banknote], ["credits", "My Kind Credits +15%", Coins], ...(BTC_ENABLED ? [["btc", "Bitcoin 75%", Bitcoin]] : [])].map(([k, label, Ic]) => (
              <button key={k} data-testid={`method-${k}`} onClick={() => setMethod(k)} className={`rounded-lg p-3 text-left border ${method === k ? "border-brand bg-white/5" : "border-rud"}`}>
                <Ic size={16} className="text-brand mb-1" /><div className="text-xs">{label}</div>
              </button>
            ))}
          </div>
          <button data-testid="earn-cashout" onClick={cashout} disabled={busy || !earn?.can_cashout} className="btn-primary w-full h-11 mt-4 flex items-center justify-center gap-2 disabled:opacity-40">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Wallet size={16} />} {earn?.can_cashout ? "Cash out now" : "Threshold not reached"}
          </button>
          <p className="text-[11px] text-sec mt-2">Payouts are simulated (demo). {BTC_ENABLED ? "Cash & Bitcoin pay out" : "Cash pays out"} <b className="text-brand">75%</b> — the remaining 25% is kept as in-app credits. The Credits option gives 15% more and stays fully in-app.</p>
        </div>

        <div className="rud-card rounded-2xl p-6" data-testid="referral-panel">
          <h3 className="font-serif text-2xl mb-1 flex items-center gap-2"><Gift className="text-brand" size={22} /> Invite & earn</h3>
          <p className="text-sec text-sm">Invite your contacts with your link. You earn <b className="text-brand">25 credits every time someone joins</b>, plus {earn?.referral_rate_pct ?? 9}% of every purchase they make. {earn?.referral_count ?? 0} joined so far.</p>
          <p className="text-[11px] text-brand mt-1" data-testid="referral-boost-note">✨ Your friends also get <b>3 bonus credits</b> to start — so everyone wins.</p>
          {user ? (
            <>
              <div className="flex items-center gap-2 bg-elevated rounded-md p-2 mt-3 font-mono text-xs break-all border border-rud">
                {referralLink}
                <button data-testid="copy-referral" onClick={() => copy(inviteMsg + "\n\n" + referralLink, "Invite message copied")} className="text-brand shrink-0"><Copy size={14} /></button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3" data-testid="invite-share">
                <a data-testid="share-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(inviteMsg + "\n\n" + referralLink)}`} target="_blank" rel="noreferrer" className="btn-outline rounded-full h-9 px-4 text-xs flex items-center gap-1.5"><Send size={13} /> WhatsApp</a>
                <a data-testid="share-email" href={`mailto:?subject=${encodeURIComponent("Join me on My Kind — learn & earn together")}&body=${encodeURIComponent(inviteMsg + "\n\n" + referralLink)}`} className="btn-outline rounded-full h-9 px-4 text-xs flex items-center gap-1.5">Email</a>
                <a data-testid="share-x" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(inviteShort)}&url=${encodeURIComponent(referralLink)}`} target="_blank" rel="noreferrer" className="btn-outline rounded-full h-9 px-4 text-xs flex items-center gap-1.5">Share on X</a>
                <button data-testid="share-native" onClick={async () => { try { if (navigator.share) { await navigator.share({ title: "My Kind — the App of Everything", text: inviteMsg, url: referralLink }); } else { copy(inviteMsg + "\n\n" + referralLink, "Invite message copied"); } } catch (_) {} }} className="btn-outline rounded-full h-9 px-4 text-xs flex items-center gap-1.5">More…</button>
              </div>
              <div className="mt-5 pt-5 border-t border-rud">
                <h4 className="font-serif text-xl mb-2 flex items-center gap-2"><Send size={16} className="text-brand" /> Transfer credits</h4>
                <div className="flex gap-2">
                  <input data-testid="xfer-to" value={xfer.to} onChange={(e) => setXfer({ ...xfer, to: e.target.value })} placeholder="Email or referral code" className="rud-input flex-1 rounded-lg px-3 py-2 text-sm" />
                  <input data-testid="xfer-amount" type="number" value={xfer.amount} onChange={(e) => setXfer({ ...xfer, amount: e.target.value })} className="rud-input rounded-lg px-2 py-2 text-sm w-24" />
                  <button data-testid="xfer-send" onClick={transfer} className="btn-primary px-4 text-sm">Send</button>
                </div>
              </div>
            </>
          ) : <button onClick={() => nav("/auth")} className="btn-primary w-full h-10 mt-4 text-sm">Sign in to get your link</button>}
        </div>
      </div>

      {/* Bonus-buy: reinvest earned bonuses into credits at 15% off */}
      <div className="rud-card rounded-2xl p-6 mt-4" data-testid="bonus-buy-panel">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-1"><Sparkles size={14} /> Reinvest your bonuses · 15% off</div>
        <p className="text-sec text-sm mb-3">Spend your earned referral &amp; data bonuses on My Kind credits and get <b className="text-brand">15% off</b>. Available bonus balance: <b className="text-[var(--rud-text)]">${earn?.total_earnings ?? 0}</b>.</p>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-sec block mb-1">Credits</label>
            <input data-testid="bonus-buy-credits" type="number" min={1} value={bonusCredits} onChange={(e) => setBonusCredits(e.target.value)} className="rud-input rounded-lg px-3 py-2 text-sm w-32" />
          </div>
          <div className="text-sm text-sec">Pay <b className="text-[var(--rud-text)]">${bonusPay}</b> from bonuses · save <b className="text-brand">${bonusSave}</b></div>
          <button data-testid="bonus-buy-submit" onClick={buyWithBonus} disabled={busy || !user} className="btn-primary h-11 px-6 flex items-center gap-2 disabled:opacity-40 ml-auto">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Coins size={16} />} Buy with bonuses
          </button>
        </div>
        <p className="text-[11px] text-sec mt-2">Uses your referral + data earnings first. Payouts are simulated (demo).</p>
      </div>

      {/* Auto-recharge settings */}
      <AutoTopupPanel />

      {/* Credit packs */}
      <div className="flex flex-wrap items-center gap-3 mt-14 mb-5">
        <h2 className="font-serif text-3xl tracking-tight">Buy Credits · $1 = 1 credit</h2>
        {pricing?.discount_pct > 0 && (
          <span data-testid="active-id-discount" className="text-xs font-semibold bg-brand text-black rounded-full px-3 py-1">{pricing.discount_pct}% {pricing.discount_label} discount applied</span>
        )}
      </div>
      <DiscountPanel onApproved={() => api.get(`/pricing?currency=${currency}`).then((r) => setPricing(r.data))} />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-5">
        {pricing?.packs.map((p) => (
          <div key={p.credits} className="rud-card rounded-2xl p-5 flex flex-col" data-testid={`pack-${p.credits}`}>
            <div className="font-serif text-3xl">{p.credits}</div><div className="text-xs text-sec">credits</div>
            <div className="mt-3 text-brand text-lg"><Price usd={p.usd} inline primaryClass="text-brand text-lg" subClass="text-[11px] text-sec" /></div>
            {p.list_usd > p.usd && <div className="text-[11px] text-sec line-through">${p.list_usd}</div>}
            {BTC_ENABLED && <div className="text-[11px] text-brand mt-1">BTC: 3% off</div>}
            <div className="mt-3 space-y-1.5">
              {BTC_ENABLED && (
                <button data-testid={`pack-btc-${p.credits}`} onClick={() => nav(`/btc?credits=${p.credits}&method=btc`)} className="btn-primary w-full h-8 rounded-full text-xs flex items-center justify-center gap-1.5"><Bitcoin size={13} /> Buy with BTC</button>
              )}
              <button data-testid={`pack-upi-${p.credits}`} onClick={() => nav(`/btc?credits=${p.credits}&method=upi`)} className="btn-outline w-full h-8 rounded-full text-xs flex items-center justify-center gap-1.5">Buy with UPI</button>
            </div>
          </div>
        ))}
      </div>

      {/* Bitcoin / UPI — unified buy page */}
      <div className="rud-card rounded-2xl p-6 mt-8 max-w-xl flex items-center justify-between gap-3" data-testid="bitcoin-panel">
        <div className="flex items-center gap-3">
          {BTC_ENABLED ? <Bitcoin className="text-brand" size={26} /> : <Coins className="text-brand" size={26} />}
          <div>
            <h3 className="font-serif text-2xl">{BTC_ENABLED ? "Pay with Bitcoin or UPI" : "Pay with UPI"}</h3>
            <p className="text-sec text-sm">{BTC_ENABLED ? "Get the wallet address, QR code, a 3% Bitcoin discount and a live quote — or pay via UPI/GPay." : "Get a UPI QR code and a live quote — pay via UPI / GPay / PhonePe."}</p>
          </div>
        </div>
        <button data-testid="open-btc-page" onClick={() => nav(BTC_ENABLED ? "/btc" : "/btc?method=upi")} className="btn-primary h-10 px-5 text-sm shrink-0">Open</button>
      </div>
    </div>
  );
}
